import type { ProviderRuntimeScope } from "../runtime-filter.js";
import {
  assertBoolean,
  assertHttpUrl,
  assertOptionalString,
  parseProviderRuntime,
  schemaError,
} from "../../schema/validate.js";
import { parseSentryFeatures, type SentryFeaturesConfig } from "./features.js";

export type {
  SentryFeaturesConfig,
  SentryTracingOptions,
  SentryReplayOptions,
  SentryProfilingOptions,
  SentryLogsOptions,
  SentryReactRouterOptions,
} from "./features.js";

export { parseSentryFeatures } from "./features.js";

export interface SentryProviderConfig {
  type: "sentry";
  id?: string;
  dsn?: string;
  environment?: string;
  release?: string;
  enabled: boolean;
  level?: "fatal" | "error" | "warning" | "info" | "debug";
  runtime: ProviderRuntimeScope;
  features?: SentryFeaturesConfig;
  initOptions?: Record<string, unknown>;
}

const SENTRY_LEVELS = new Set(["fatal", "error", "warning", "info", "debug"]);

export function parseSentryProviderConfig(input: unknown): SentryProviderConfig {
  if (typeof input !== "object" || input === null || Array.isArray(input)) {
    throw schemaError("provider must be an object");
  }

  const raw = input as Record<string, unknown>;
  if (raw.type !== "sentry") {
    throw schemaError('provider type must be "sentry"');
  }

  const level = raw.level;
  if (level !== undefined && !SENTRY_LEVELS.has(level as string)) {
    throw schemaError("sentry level is invalid");
  }
  const dsn = raw.dsn;
  if (dsn !== undefined) {
    assertHttpUrl(dsn, "dsn");
  }
  const initOptions = raw.initOptions;
  if (
    initOptions !== undefined &&
    (typeof initOptions !== "object" || initOptions === null || Array.isArray(initOptions))
  ) {
    throw schemaError("initOptions must be an object");
  }

  return {
    type: "sentry",
    id: assertOptionalString(raw.id, "id"),
    dsn: dsn as string | undefined,
    environment: assertOptionalString(raw.environment, "environment"),
    release: assertOptionalString(raw.release, "release"),
    enabled: assertBoolean(raw.enabled, "enabled") ?? true,
    level: level as SentryProviderConfig["level"] | undefined,
    runtime: parseProviderRuntime(raw.runtime),
    features: parseSentryFeatures(raw.features),
    initOptions: initOptions as Record<string, unknown> | undefined,
  };
}
